export const SURVEY_RATING_OPTIONS = [
  {
    value: 1,
    emoji: '😠',
    label: 'Very Dissatisfied',
    description: 'Poor experience, needs major improvement',
  },
  {
    value: 2,
    emoji: '😕',
    label: 'Dissatisfied',
    description: 'Below expectations, some issues present',
  },
  {
    value: 3,
    emoji: '😐',
    label: 'Neutral',
    description: 'Average, acceptable but not outstanding',
  },
  {
    value: 4,
    emoji: '🙂',
    label: 'Satisfied',
    description: 'Good experience, met expectations',
  },
  {
    value: 5,
    emoji: '🤩',
    label: 'Very Satisfied',
    description: 'Excellent experience, exceeded expectations',
  },
] as const;

export const SURVEY_MAX_QUESTION_OPTIONS = 9;
export const SURVEY_MAX_SMILEY_TEXT_LENGTH = 40;
export const SURVEY_MAX_OPTION_LENGTH = 60;

export const SURVEY_SMILEY_PALETTE = [
  '😠',
  '😡',
  '😞',
  '😢',
  '😕',
  '🙁',
  '😣',
  '😐',
  '😶',
  '🤔',
  '🙂',
  '😊',
  '😄',
  '😁',
  '🤩',
  '😍',
  '🥰',
  '👎',
  '👍',
  '👏',
  '🙌',
  '👌',
  '💔',
  '❤️',
  '⭐',
  '🔥',
  '💯',
  '✅',
  '❌',
  '🎉',
];

export type SurveySmileyForm = {
  emoji: string;
  text: string;
};

export const createDefaultSmileys = (): SurveySmileyForm[] =>
  SURVEY_RATING_OPTIONS.map((r) => ({emoji: r.emoji, text: r.label}));

export const normalizeSmileys = (raw: any): SurveySmileyForm[] => {
  if (!Array.isArray(raw) || !raw.length) return createDefaultSmileys();
  const mapped = raw
    .map((s: any) => ({
      emoji: String(s?.emoji ?? s?.icon ?? s?.smiley ?? '').trim() || '😐',
      text: String(s?.text ?? s?.label ?? s?.smileyText ?? '').trim().slice(0, SURVEY_MAX_SMILEY_TEXT_LENGTH),
    }))
    .filter((s: SurveySmileyForm) => s.emoji || s.text)
    .slice(0, SURVEY_MAX_QUESTION_OPTIONS);
  return mapped.length ? mapped : createDefaultSmileys();
};

export type SurveyRatingChoice = {
  value: number;
  emoji: string;
  label: string;
  description: string;
};

export const ratingChoicesForQuestion = (q?: {
  showSmileys?: boolean;
  smileys?: SurveySmileyForm[];
}): SurveyRatingChoice[] => {
  const smileys = normalizeSmileys(q?.smileys);
  const showIcons = q?.showSmileys !== false;
  return smileys.map((s, i) => ({
    value: i + 1,
    emoji: showIcons ? s.emoji : '',
    label: s.text || `Option ${i + 1}`,
    description: s.text || '',
  }));
};

export const lowRatingMaxForScale = (scaleLength: number) =>
  scaleLength <= 5 ? 3 : Math.max(1, Math.ceil(scaleLength * 0.6));

export const isLowSurveyRating = (value: number, scaleLength: number) =>
  value > 0 && value <= lowRatingMaxForScale(scaleLength);

export const surveyRatingEmoji = (rating?: number | null) =>
  SURVEY_RATING_OPTIONS.find((r) => r.value === rating)?.emoji || '—';

export const surveyRatingLabel = (rating?: number | null) =>
  SURVEY_RATING_OPTIONS.find((r) => r.value === rating)?.label || '';

export const surveyRatingDescription = (rating?: number | null) =>
  SURVEY_RATING_OPTIONS.find((r) => r.value === rating)?.description || '';

export const surveyRatingDisplay = (rating?: number | null) => {
  const item = SURVEY_RATING_OPTIONS.find((r) => r.value === rating);
  if (!item) return '—';
  return `${item.emoji} ${item.label}`;
};

export const surveyRatingFullLabel = (rating?: number | null) => {
  const item = SURVEY_RATING_OPTIONS.find((r) => r.value === rating);
  if (!item) return '';
  return `${item.label} – ${item.description}`;
};
